import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { customerAPI, billingAPI } from "../lib/api";
import { BillingProfileEditor } from "../components/billing/BillingProfileEditor";
import { BillingProfileSummary } from "../components/billing/BillingProfileSummary";
import { CatalogManager } from "../components/billing/CatalogManager";
import { XeroSync } from "../components/billing/XeroSync";
import { CompanyProfitOverview } from "../components/billing/CompanyProfitOverview";
import { MonthlyRenewals } from "../components/billing/MonthlyRenewals";
import { DollarSign, Settings2, Pencil, Trash2, Plus, RefreshCw, CalendarClock, Users } from "lucide-react";
import { toast } from "sonner";

export function ClientBillingProfile() {
  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [mode, setMode] = useState("view"); // view | edit
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [xeroOpen, setXeroOpen] = useState(false);
  const [overviewRefresh, setOverviewRefresh] = useState(0);
  const [activeTab, setActiveTab] = useState("clients");

  const jumpToCustomer = (customerId) => {
    setActiveTab("clients");
    handleCustomerChange(customerId);
  };

  const fetchCatalogs = useCallback(async () => {
    try {
      const [servicesRes, licensesRes] = await Promise.all([
        billingAPI.getServices(),
        billingAPI.getLicenseTypes(),
      ]);
      setCatalog(servicesRes.data);
      setLicenseTypes(licensesRes.data);
    } catch (err) {
      toast.error("Failed to load services catalog");
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await customerAPI.getAll();
        setCustomers(res.data.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        toast.error("Failed to load customers");
      }
    };
    init();
    fetchCatalogs();
    const params = new URLSearchParams(window.location.search);
    const xero = params.get("xero");
    if (xero === "connected") {
      toast.success("Connected to Xero successfully");
      setXeroOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (xero === "error") {
      toast.error(`Xero connection failed (${params.get("reason") || "unknown"})`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchCatalogs]);

  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  const handleCustomerChange = async (customerId) => {
    setSelectedCustomerId(customerId);
    setProfile(null);
    setMode("view");
    setProfileLoading(true);
    try {
      const res = await billingAPI.getProfileByCustomer(customerId);
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setProfile(null); // No profile yet
      } else {
        toast.error("Failed to load billing profile");
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm(`Delete the billing profile for ${selectedCustomer?.name}? This cannot be undone.`)) return;
    try {
      await billingAPI.deleteProfile(selectedCustomerId);
      setProfile(null);
      setMode("view");
      setOverviewRefresh((n) => n + 1);
      toast.success("Billing profile deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete billing profile");
    }
  };

  return (
    <div className="space-y-6" data-testid="client-billing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            Client Billing Profile
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Track the services each customer subscribes to and what they pay.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setXeroOpen(true)} data-testid="xero-sync-button">
            <RefreshCw className="w-4 h-4 mr-2 text-[#13b5ea]" />
            Sync from Xero
          </Button>
          <Button variant="outline" onClick={() => setCatalogOpen(true)} data-testid="manage-services-button">
            <Settings2 className="w-4 h-4 mr-2" />
            Manage Services
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <TabsList data-testid="billing-tabs">
          <TabsTrigger value="clients" data-testid="billing-tab-clients">
            <Users className="w-4 h-4 mr-1.5" /> Client Profiles
          </TabsTrigger>
          <TabsTrigger value="renewals" data-testid="billing-tab-renewals">
            <CalendarClock className="w-4 h-4 mr-1.5" /> Renewals
          </TabsTrigger>
        </TabsList>

        <TabsContent value="renewals" className="mt-0">
          <MonthlyRenewals refreshKey={overviewRefresh} onSelectCustomer={jumpToCustomer} />
        </TabsContent>

        <TabsContent value="clients" className="mt-0 space-y-5">
          {/* Company-wide profit overview */}
          <CompanyProfitOverview refreshKey={overviewRefresh} onSelectCustomer={jumpToCustomer} />

          {/* Customer selector */}
      <Card>
        <CardContent className="pt-5 pb-5">
          <Label className="text-sm font-medium">Customer</Label>
          <Select value={selectedCustomerId} onValueChange={handleCustomerChange}>
            <SelectTrigger className="mt-1 max-w-md" data-testid="billing-customer-select">
              <SelectValue placeholder="Select a customer to view or build their billing profile..." />
            </SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Body */}
      {!selectedCustomerId ? (
        <Card>
          <CardContent className="py-16 text-center" data-testid="billing-empty-state">
            <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Select a customer above to get started.</p>
          </CardContent>
        </Card>
      ) : profileLoading ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-500">Loading billing profile...</CardContent>
        </Card>
      ) : mode === "edit" ? (
        <BillingProfileEditor
          customer={selectedCustomer}
          catalog={catalog}
          licenseTypes={licenseTypes}
          initialServices={profile?.services || []}
          onCatalogRefresh={fetchCatalogs}
          onSaved={(saved) => {
            setProfile(saved);
            setMode("view");
            setOverviewRefresh((n) => n + 1);
          }}
          onCancel={() => setMode("view")}
        />
      ) : profile ? (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMode("edit")} data-testid="edit-profile-button">
              <Pencil className="w-4 h-4 mr-2" /> Edit Profile
            </Button>
            <Button
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={handleDeleteProfile}
              data-testid="delete-profile-button"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          </div>
          <BillingProfileSummary customer={selectedCustomer} profile={profile} />
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center" data-testid="billing-no-profile-state">
            <p className="text-slate-600 font-medium mb-1">No billing profile yet for {selectedCustomer?.name}</p>
            <p className="text-sm text-slate-500 mb-5">Build one by selecting their services, charges and billing frequency.</p>
            <Button onClick={() => setMode("edit")} className="bg-blue-600 hover:bg-blue-700" data-testid="create-profile-button">
              <Plus className="w-4 h-4 mr-2" /> Create Billing Profile
            </Button>
          </CardContent>
        </Card>
      )}
        </TabsContent>
      </Tabs>

      <CatalogManager
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        services={catalog}
        licenseTypes={licenseTypes}
        onRefresh={fetchCatalogs}
      />

      <XeroSync
        open={xeroOpen}
        onOpenChange={setXeroOpen}
        customers={customers}
        onApplied={() => {
          fetchCatalogs();
          setOverviewRefresh((n) => n + 1);
          if (selectedCustomerId) handleCustomerChange(selectedCustomerId);
        }}
      />
    </div>
  );
}
